import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Tooltip,
} from '@atgfr8/atg-ui-tailwind';
import { EyeIcon, InformationIcon } from '@atgfr8/icons';
import Button from '@components/Button/Button';
import { SelectInputCustom } from '@components/Select/SelectInputCustom';
import {
  MAX_PORTAL_CREDENTIALS_LENGTH,
  PORTAL_SCHEDULING_METHOD,
  schedulingEmailCcError,
} from '@common/facilityDetails';
import {
  appointmentTypeOptions,
  schedulingMethodOptions,
} from '@views/customer/customerDetail/constants';
import { createSignal, Show } from 'solid-js';

const NOT_SPECIFIED = '__not_specified__';

export type SchedulingMethodValue = {
  appointmentType: string;
  schedulingMethod: string;
  schedulingMethodDetails: string;
  schedulingEmailCc: string | null;
  portalUrl: string;
  portalCredentials: string;
};

type SchedulingMethodFieldsProps = {
  layout: 'inline' | 'stacked';
  appointmentLabel: string;
  value: () => SchedulingMethodValue;
  onChange: (value: SchedulingMethodValue) => void;
  variant: 'address-book' | 'stop';
  disabled?: boolean;
};

const testIds = {
  'address-book': {
    appointmentType: 'address-book-facility-appointment-type-select',
    schedulingMethod: 'address-book-facility-scheduling-method-select',
    details: 'address-book-facility-scheduling-details-textInput',
    email: 'address-book-facility-scheduling-email-textInput',
    ccEmail: 'address-book-facility-scheduling-cc-textInput',
    ccTooltip: 'address-book-facility-cc-tooltip',
    portalUrl: 'address-book-facility-portal-url-textInput',
    portalCredentialsMasked: 'address-book-facility-portal-credentials-masked',
    portalCredentialsReveal: 'address-book-facility-portal-credentials-reveal',
    portalCredentials: 'address-book-facility-portal-credentials-textInput',
    portalCredentialsDone: 'address-book-facility-portal-credentials-done',
  },
  stop: {
    appointmentType: 'facility-details-weekly-appointment-type',
    schedulingMethod: 'facility-details-weekly-scheduling-method',
    details: 'facility-details-weekly-scheduling-details',
    email: 'facility-details-scheduling-email',
    ccEmail: 'facility-details-scheduling-cc',
    ccTooltip: 'facility-details-cc-tooltip',
    portalUrl: 'facility-details-portal-url',
    portalCredentialsMasked: 'facility-details-portal-credentials-masked',
    portalCredentialsReveal: 'facility-details-portal-credentials-reveal',
    portalCredentials: 'facility-details-portal-credentials',
    portalCredentialsDone: 'facility-details-portal-credentials-done',
  },
};

export const SchedulingMethodFields = (props: SchedulingMethodFieldsProps) => {
  const [showPortalLogin, setShowPortalLogin] = createSignal(false);
  const ids = () => testIds[props.variant];

  const changeMethod = (method: string) => {
    const current = props.value();
    if (method === current.schedulingMethod) return;
    const stayingOnPortal = method === PORTAL_SCHEDULING_METHOD;
    props.onChange({
      ...current,
      schedulingMethod: method,
      schedulingMethodDetails: '',
      schedulingEmailCc: null,
      portalUrl: stayingOnPortal ? current.portalUrl : '',
      portalCredentials: stayingOnPortal ? current.portalCredentials : '',
    });
  };

  const patch = (updates: Partial<SchedulingMethodValue>) => {
    props.onChange({
      ...props.value(),
      ...updates,
    });
  };

  const ccError = () => {
    const value = props.value().schedulingEmailCc;
    if (value == null || value.length === 0) return undefined;
    return schedulingEmailCcError(value, true) ?? undefined;
  };

  return (
    <div
      class={
        props.layout === 'inline'
          ? 'grid grid-cols-1 items-start gap-4 xl:grid-cols-2'
          : 'flex flex-col gap-3'
      }
    >
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SelectInputCustom
          label={props.appointmentLabel}
          menuItems={appointmentTypeOptions}
          value={props.value().appointmentType}
          disabled={props.disabled}
          onChange={(val: string) => patch({ appointmentType: val })}
          dataTestId={ids().appointmentType}
        />
        <SelectInputCustom
          label="Scheduling Method (optional)"
          menuItems={[
            { label: 'Not specified', value: NOT_SPECIFIED },
            ...schedulingMethodOptions,
          ]}
          value={props.value().schedulingMethod || NOT_SPECIFIED}
          disabled={props.disabled}
          onChange={(val: string) =>
            changeMethod(val === NOT_SPECIFIED ? '' : val)
          }
          dataTestId={ids().schedulingMethod}
        />
      </div>
      <div class="min-w-0">
        <Show
          when={props.value().schedulingMethod === PORTAL_SCHEDULING_METHOD}
        >
          <TextField
            label="Details"
            placeholder="Website URL"
            fullWidth
            value={props.value().portalUrl}
            disabled={props.disabled}
            onChange={(value: string) => patch({ portalUrl: value })}
            maxLength={500}
            dataTestId={ids().portalUrl}
          />
          <Box class="mt-3 flex items-end gap-2">
            <Box class="min-w-0 flex-1">
              <TextField
                label="Portal Login Info"
                placeholder="No login info saved"
                value={props.value().portalCredentials ? '••••••••••••' : ''}
                disabled
                dataTestId={ids().portalCredentialsMasked}
              />
            </Box>
            <Button
              variant="text"
              type="button"
              label={props.value().portalCredentials ? 'View' : 'Add'}
              startIcon={<EyeIcon class="size-4" />}
              onClick={() => setShowPortalLogin(true)}
              dataTestId={ids().portalCredentialsReveal}
            />
          </Box>
          <Dialog
            open={showPortalLogin()}
            onClose={() => setShowPortalLogin(false)}
            id={`${props.variant}-portal-login-modal`}
          >
            <DialogContent class="pt-5">
              <TextField
                multiline
                rows={6}
                label="Portal Login Info"
                placeholder="Username, password, or JSON credential record"
                value={props.value().portalCredentials}
                disabled={props.disabled}
                onChange={(value: string) =>
                  patch({ portalCredentials: value })
                }
                maxLength={MAX_PORTAL_CREDENTIALS_LENGTH}
                dataTestId={ids().portalCredentials}
              />
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                label="Done"
                type="button"
                onClick={() => setShowPortalLogin(false)}
                dataTestId={ids().portalCredentialsDone}
              />
            </DialogActions>
          </Dialog>
        </Show>
        <Show when={props.value().schedulingMethod === 'Email'}>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="Scheduling Email(s)"
              placeholder="Enter email address(s)"
              helperText="For multiple emails, separate by comma."
              fullWidth
              value={props.value().schedulingMethodDetails}
              disabled={props.disabled}
              onChange={(value: string) =>
                patch({ schedulingMethodDetails: value })
              }
              maxLength={500}
              dataTestId={ids().email}
            />
            <div class="flex items-start gap-1">
              <div class="min-w-0 flex-1">
                <TextField
                  label="CC Scheduling Email(s)"
                  placeholder="Enter email address"
                  fullWidth
                  value={props.value().schedulingEmailCc ?? ''}
                  disabled={props.disabled}
                  error={ccError()}
                  onChange={(value: string) =>
                    patch({
                      schedulingEmailCc: value.length === 0 ? null : value,
                    })
                  }
                  maxLength={100}
                  dataTestId={ids().ccEmail}
                />
              </div>
              <Tooltip
                title="Emails entered here will be CC'd on all scheduling emails. This is most commonly used to provide internal visibility."
                dataTestId={ids().ccTooltip}
              >
                <span class="mt-2 inline-flex text-primary">
                  <InformationIcon size={16} />
                </span>
              </Tooltip>
            </div>
          </div>
        </Show>
        <Show when={props.value().schedulingMethod === 'Phone'}>
          <TextField
            label="Details"
            placeholder="Phone number"
            fullWidth
            value={props.value().schedulingMethodDetails}
            disabled={props.disabled}
            onChange={(value: string) =>
              patch({ schedulingMethodDetails: value })
            }
            maxLength={500}
            dataTestId={ids().details}
          />
        </Show>
        <Show when={props.value().schedulingMethod === 'Other'}>
          <TextField
            label="Details"
            placeholder="Enter scheduling details"
            fullWidth
            multiline
            rows={3}
            value={props.value().schedulingMethodDetails}
            disabled={props.disabled}
            onChange={(value: string) =>
              patch({ schedulingMethodDetails: value })
            }
            maxLength={500}
            dataTestId={ids().details}
          />
        </Show>
      </div>
    </div>
  );
};
